import React, { useState, useEffect, useRef } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import Button from '@weave-design/button';
import Typography from '@weave-design/typography';
import Spacer from '@weave-design/spacer';
import Dropdown from '@weave-design/dropdown';
import Surface from '@weave-design/surface';
import Divider from '@weave-design/divider';

import { DataRefresh16, Filter24, CloseMUI } from '@weave-design/icons';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ThemeContext } from '@weave-design/theme-context';
import { getDashboardData, getNavigationData, getHostingProjects, getRecentlyExecutedJobs, getNumberOfJobExecuted, getTopMostFailures, getTopFailingJobs, getNumberOfJobs, getNumberOfTestSuites, getTestsuiteExecutions, getExecutionIterationDetails, getLastRefreshTime } from '../../utils/api.service';
import LoadingSpinner from '../LoadingSpinner';
import SearchableDropdown from '../SearchableDropdown/SearchableDropdown';
import ProjectSelection from '../ProjectSelection/ProjectSelection';
import '@weave-design/fonts/build/ArtifaktElement.css';
import './DashboardV2.scss';

// Import theme data - using the JSON structure we found
const lightGrayMediumTheme = require('@weave-design/theme-data/build/json/lightGrayMediumDensityTheme/theme.json');

// API Limits Configuration
const TOP_FAILURES_API_LIMIT = 50;
const TOP_FAILING_JOBS_API_LIMIT = 50;


// Utility functions from Dashboard.js
const getDateRange = (dateRange) => {
  if (dateRange && dateRange.startDate && dateRange.endDate) {
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    };
  }

  // Default to last 7 days
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 7);

  return {
    startDate: startDate.toISOString().split('T')[0], // Format: YYYY-MM-DD
    endDate: endDate.toISOString().split('T')[0]
  };
};

const formatRefreshTime = (timestamp) => {
  if (!timestamp) return 'Never';

  const date = new Date(timestamp);

  // Format to get the main parts: "Aug 7, 2025, 5:32:01 PM"
  const dateTimeString = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(date);

  // Extract the timezone offset string like "GMT+05:30"
  const dateStringForOffset = date.toString();
  const gmtMatch = dateStringForOffset.match(/GMT([+-]\d{2})(\d{2})/);

  let gmtPart = '';
  if (gmtMatch) {
    gmtPart = ` GMT${gmtMatch[1]}:${gmtMatch[2]}`;
  }

  return `${dateTimeString}${gmtPart}`;
};

// Utility function to clear old cache entries when localStorage is full
const clearOldCacheEntries = (prefix) => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    
    // Remove old cache entries (keep only the most recent ones)
    keysToRemove.slice(0, Math.max(0, keysToRemove.length - 5)).forEach(key => {
      localStorage.removeItem(key);
      localStorage.removeItem(key + '-time'); // Also remove time keys
    });
    
    console.log(`🧹 [CACHE] Cleared ${keysToRemove.length} old cache entries with prefix: ${prefix}`);
  } catch (error) {
    console.warn('⚠️ [CACHE] Failed to clear old cache entries:', error);
  }
};

const DashboardV2 = () => {
  // Add performance timing for dashboard initialization
  const dashboardStartTime = useRef(performance.now());
  
  const { emailid: emailId, displayname: displayName } = useSelector(
    (state) => state.app?.userInfo || {}
  );

  const categories = useSelector((state) => state.menu?.categories || {});
  const history = useHistory();
  const location = useLocation();

  // Applied filters (what's actually being used for data and shown in "Applied Filters")
  const [appliedFilters, setAppliedFilters] = useState({
    org: '',
    workstream: '',
    projects: [],
    team: '',
    dateRange: getDateRange()
  });

  // Draft filter selections (what user is currently editing in the panel)
  const [draftFilters, setDraftFilters] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [recentlyExecutedJobs, setRecentlyExecutedJobs] = useState([]);
  const [topFailures, setTopFailures] = useState([]);
  const [topFailingJobs, setTopFailingJobs] = useState([]);
  const [testSuiteTrendData, setTestSuiteTrendData] = useState([]);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [isLoadingTopFailures, setIsLoadingTopFailures] = useState(false);
  const [isLoadingTopFailingJobs, setIsLoadingTopFailingJobs] = useState(false);
  const [isLoadingDashboardData, setIsLoadingDashboardData] = useState(false);

  const [showFilters, setShowFilters] = useState(false);

  // State for dropdown options from navigation data
  const [navigationData, setNavigationData] = useState(null);
  const [isLoadingNavigation, setIsLoadingNavigation] = useState(false);
  const [navigationDataLoaded, setNavigationDataLoaded] = useState(false);

  // State for hosting project data
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectCache, setProjectCache] = useState({});
  const [projectClearCounter, setProjectClearCounter] = useState(0);
  const [projectMapping, setProjectMapping] = useState({}); // Maps displayname to projectname

  // State to track if filters have been modified since last apply
  const [filtersModified, setFiltersModified] = useState(false);

  // State to track if initial data has been loaded to prevent re-triggering
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // State to force re-render of dropdowns when reverting
  const [dropdownKey, setDropdownKey] = useState(0);

  // State to track loading of test suite execution data
  const [isLoadingTestSuiteData, setIsLoadingTestSuiteData] = useState(false);
  const [apiSuiteData, setApiSuiteData] = useState([]);

  const [suiteExecutionCounts, setSuiteExecutionCounts] = useState([]);

  // State for iteration details view (new chart)
  const [activeChartView, setActiveChartView] = useState('execution-trends'); // 'execution-trends' or 'iteration-details'
  const [isLoadingIterationDetails, setIsLoadingIterationDetails] = useState(false);
  const [apiIterationData, setApiIterationData] = useState([]);
  const [iterationChartData, setIterationChartData] = useState([]);

  // State for pie chart view toggle (jobs vs iterations)
  const [pieChartView, setPieChartView] = useState('jobs'); // 'jobs' or 'iterations'

  // State to track if project mapping is ready
  const [projectMappingReady, setProjectMappingReady] = useState(false);

  // State to track overall dashboard loading (for URL sharing and filter changes)
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const loadingTimeoutRef = useRef(null);
  const hasInitializedRef = useRef(false);
  
  // Add refs to track loading states and prevent duplicates
  const navigationLoadingRef = useRef(false);
  const projectLoadingRef = useRef(new Set()); // Track loading by org-workstream key
  const lastDataLoadParams = useRef(''); // Track last data load parameters

  const LoadingIndicator = ({ progress }) => (
    <div className="dashboard-loading-overlay">
      <div className="loading-content">
        <LoadingSpinner />
        <div className="loading-text">
          <h3>Loading Dashboard Data</h3>
          <p>Fetching data for your selected filters...</p>
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="progress-text">{progress}%</div>
        </div>
        <div className="loading-steps">
          <div className={`loading-step ${progress >= 10 ? 'complete' : ''}`}>
            <span className="step-icon">📊</span>
            <span>Loading job statistics</span>
          </div>
          <div className={`loading-step ${progress >= 30 ? 'complete' : ''}`}>
            <span className="step-icon">📈</span>
            <span>Fetching test suite data</span>
          </div>
          <div className={`loading-step ${progress >= 80 ? 'complete' : ''}`}>
            <span className="step-icon">🔍</span>
            <span>Analyzing failure trends</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Pagination state
  const [currentFailuresPage, setCurrentFailuresPage] = useState(1);
  const [currentFailingJobsPage, setCurrentFailingJobsPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Filter Options
  const [orgOptions, setOrgOptions] = useState([]);
  const [allWorkstreams, setAllWorkstreams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);

  const [activeFilterTags, setActiveFilterTags] = useState([]);
  const [clearValue, setClearValue] = useState(false);
  const [selectedTestSuite, setSelectedTestSuite] = useState('');
  const [drilldownChartData, setDrilldownChartData] = useState([]);
  const [drilldownStatusFilter, setDrilldownStatusFilter] = useState('all');
  // Tooltip visibility state for constrained removals
  const [workstreamTagTooltipVisible, setWorkstreamTagTooltipVisible] = useState(false);
  const workstreamTooltipTimerRef = useRef(null);

  const handleDrilldownStatusChange = (value) => {
    setDrilldownStatusFilter(value);
  };

  // This effect recalculates the chart data when the status filter changes.
  useEffect(() => {
    if (apiSuiteData && apiSuiteData.length > 0 && selectedTestSuite) {
      const chartData = transformSuiteDataForChart(apiSuiteData, selectedTestSuite);
      setDrilldownChartData(chartData);
    } else {
      setDrilldownChartData([]);
    }
  }, [apiSuiteData, selectedTestSuite, drilldownStatusFilter]); // Re-run when filter changes

  // Debug effect to track state changes for troubleshooting
  useEffect(() => {
    console.log('🔍 [STATE_DEBUG] Data state updated:', {
      topFailures: topFailures.length,
      topFailingJobs: topFailingJobs.length,
      suiteExecutionCounts: suiteExecutionCounts.length,
      apiSuiteData: apiSuiteData ? apiSuiteData.length : 'null',
      drilldownChartData: drilldownChartData.length
    });
  }, [topFailures, topFailingJobs, suiteExecutionCounts, apiSuiteData, drilldownChartData]);


  // Computed filtered options based on draft selections (use applied filters if draft not initialized)
  const currentFilters = draftFilters || appliedFilters;
  const workstreamOptions = currentFilters.org ?
    allWorkstreams.filter(ws => ws.orgName === currentFilters.org) : [];

  const teamOptions = currentFilters.workstream ?
    allTeams.filter(team => team.workstreamName === currentFilters.workstream) : [];

  const transformSuiteDataForChart = (apiResponse, selectedSuite) => {
    const suiteData = apiResponse.find(s => s.testsuitename === selectedSuite);
    if (!suiteData || !suiteData.DayWiseExecutions) {
      console.log('🔍 [CHART_DATA] No suite data found for:', selectedSuite);
      return [];
    }

    const dayEntries = Object.entries(suiteData.DayWiseExecutions).sort(([dateA], [dateB]) => {
      const [dayA, monthA, yearA] = dateA.split('-');
      const [dayB, monthB, yearB] = dateB.split('-');
      return new Date(`20${yearA}`, monthA - 1, dayA) - new Date(`20${yearB}`, monthB - 1, dayB);
    });

    console.log('🔍 [CHART_DATA] Processing suite data for:', selectedSuite, {
      totalDays: dayEntries.length,
      dateRange: dayEntries.length > 0 ? `${dayEntries[0][0]} to ${dayEntries[dayEntries.length - 1][0]}` : 'No dates',
      totalRuns: dayEntries.reduce((sum, [, runs]) => sum + runs.length, 0)
    });

    const chartData = [];
    let runCounter = 1;

    dayEntries.forEach(([dateStr, runs], dayIndex) => {
      const [day, month, year] = dateStr.split('-');
      const date = new Date(`20${year}`, parseInt(month, 10) - 1, parseInt(day, 10));
      const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      // Fix timezone issue - format date manually instead of using toISOString()
      const fullDate = `20${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

      runs.forEach(run => {
        chartData.push({
          date: formattedDate,
          fullDate: fullDate,
          run: `Run ${runCounter++}`,
          successful: run.passed_test || 0,
          failed: run.failed_test || 0,
          total: (run.passed_test || 0) + (run.failed_test || 0),
          executionId: run.executionid || run.execution_id || run.executionId,
          tools: run.tools || run.tool
        });
      });

      // Add a gap after each day's runs, but not for the last day
      if (dayIndex < dayEntries.length - 1) {
        chartData.push({
          date: '',
          run: `gap-${dayIndex}`,
          successful: 0,
          failed: 0,
          total: 0,
          gap: true
        });
      }
    });

    console.log('✅ [CHART_DATA] Final chart data generated:', {
      totalDataPoints: chartData.length,
      actualRuns: chartData.filter(d => !d.gap).length,
      gaps: chartData.filter(d => d.gap).length,
      firstDate: chartData.length > 0 ? chartData[0].fullDate : 'None',
      lastDate: chartData.length > 0 ? chartData[chartData.length - 1].fullDate : 'None'
    });

    return chartData;
  };

  const transformIterationDataForChart = (apiResponse, selectedSuite) => {
    const suiteData = apiResponse.find(s => s.testsuitename === selectedSuite);
    if (!suiteData || !suiteData.DayWiseExecutions) {
      console.log('🔍 [ITERATION_CHART] No iteration data found for:', selectedSuite);
      return [];
    }

    const dayEntries = Object.entries(suiteData.DayWiseExecutions).sort(([dateA], [dateB]) => {
      const [dayA, monthA, yearA] = dateA.split('-');
      const [dayB, monthB, yearB] = dateB.split('-');
      return new Date(`20${yearA}`, monthA - 1, dayA) - new Date(`20${yearB}`, monthB - 1, dayB);
    });

    console.log('🔍 [ITERATION_CHART] Processing iteration data for:', selectedSuite, {
      totalDays: dayEntries.length,
      dateRange: dayEntries.length > 0 ? `${dayEntries[0][0]} to ${dayEntries[dayEntries.length - 1][0]}` : 'No dates',
      totalRuns: dayEntries.reduce((sum, [, runs]) => sum + runs.length, 0)
    });

    const chartData = [];
    let runCounter = 1;

    dayEntries.forEach(([dateStr, runs], dayIndex) => {
      const [day, month, year] = dateStr.split('-');
      const date = new Date(`20${year}`, parseInt(month, 10) - 1, parseInt(day, 10));
      const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      // Fix timezone issue - format date manually instead of using toISOString()
      const fullDate = `20${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

      runs.forEach(run => {
        chartData.push({
          date: formattedDate,
          fullDate: fullDate,
          run: `Run ${runCounter++}`,
          successIterations: run.total_success_iterations || 0,
          failedIterations: run.total_failed_iterations || 0,
          inProgressIterations: run.total_inprogress_iterations || 0,
          skippedIterations: run.total_skipped_iterations || 0,
          totalIterations: run.total_iterations || 0,
          executionId: run.executionid || run.execution_id || run.executionId,
          tools: run.tools || run.tool,
          status: run.status,
          time: run.time
        });
      });

      // Add a gap after each day's runs, but not for the last day
      if (dayIndex < dayEntries.length - 1) {
        chartData.push({
          date: '',
          run: `gap-${dayIndex}`,
          successIterations: 0,
          failedIterations: 0,
          inProgressIterations: 0,
          skippedIterations: 0,
          totalIterations: 0,
          gap: true
        });
      }
    });

    console.log('✅ [ITERATION_CHART] Final iteration chart data generated:', {
      totalDataPoints: chartData.length,
      actualRuns: chartData.filter(d => !d.gap).length,
      gaps: chartData.filter(d => d.gap).length,
      firstDate: chartData.length > 0 ? chartData[0].fullDate : 'None',
      lastDate: chartData.length > 0 ? chartData[chartData.length - 1].fullDate : 'None'
    });

    return chartData;
  };

  useEffect(() => {
    if (suiteExecutionCounts.length > 0) {
      const suiteNames = suiteExecutionCounts.map(s => s.name);
      if (!suiteNames.includes(selectedTestSuite) || selectedTestSuite === '') {
        setSelectedTestSuite(suiteNames[0]);
      }
    } else {
      setSelectedTestSuite('');
    }
  }, [suiteExecutionCounts]);

  useEffect(() => {
    if (apiSuiteData && apiSuiteData.length > 0 && selectedTestSuite) {
      const chartData = transformSuiteDataForChart(apiSuiteData, selectedTestSuite);
      setDrilldownChartData(chartData);
    } else {
      setDrilldownChartData([]);
    }
  }, [apiSuiteData, selectedTestSuite]);

  // Transform iteration data when loaded or test suite changes
  useEffect(() => {
    if (apiIterationData && apiIterationData.length > 0 && selectedTestSuite) {
      const chartData = transformIterationDataForChart(apiIterationData, selectedTestSuite);
      setIterationChartData(chartData);
    } else {
      setIterationChartData([]);
    }
  }, [apiIterationData, selectedTestSuite]);

  // Custom X-Axis Tick component for month labels only
  const CustomizedXAxisTick = (props) => {
    const { x, y, payload, index, data } = props;
    const currentData = data[index];
    if (!currentData || currentData.gap) return null;

    const { fullDate } = currentData;
    if (!fullDate) return null;

    // Parse the current date
    const currentDate = new Date(fullDate);
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // Check if this is the first occurrence of a new month
    let showMonthLabel = false;
    if (index === 0) {
      // Always show the first month
      showMonthLabel = true;
    } else {
      // Look for previous non-gap entries to find the last month shown
      for (let i = index - 1; i >= 0; i--) {
        const prevData = data[i];
        if (prevData && !prevData.gap && prevData.fullDate) {
          const prevDate = new Date(prevData.fullDate);
          const prevMonth = prevDate.getMonth();
          const prevYear = prevDate.getFullYear();
          
          // Show label if month or year changed
          if (currentMonth !== prevMonth || currentYear !== prevYear) {
            showMonthLabel = true;
          }
          break;
        }
      }
    }

    return (
      <g transform={`translate(${x},${y})`}>
        {showMonthLabel && (
          <text x={0} y={0} dy={16} textAnchor="middle" fill="#1f2937" style={{ fontSize: '11px', fontWeight: '600' }}>
            {currentDate.toLocaleDateString('en-US', { month: 'short' })} '{currentDate.getFullYear().toString().slice(-2)}
          </text>
        )}
      </g>
    );
  };

  // Compute real data from API responses
  const jobsOverviewData = dashboardData || {
    totalJobsAvailable: 0,
    numberOfTestSuites: 0,
    totalIterationJobs: 0,
    jobsStatusCount: {
      numberOfJobsPassed: 0,
      numberOfJobsFailed: 0,
      numberOfJobsInProgress: 0,
      numberOfJobsInQueue: 0,
    },
    iterationsStatusCount: {
      numberOfIterationsPassed: 0,
      numberOfIterationsFailed: 0,
    }
  };

  const { numberOfJobsPassed = 0, numberOfJobsFailed = 0 } = jobsOverviewData.jobsStatusCount;
  const totalExecutedJobs = numberOfJobsPassed + numberOfJobsFailed;

  const { numberOfIterationsPassed = 0, numberOfIterationsFailed = 0 } = jobsOverviewData.iterationsStatusCount;
  const totalExecutedIterations = numberOfIterationsPassed + numberOfIterationsFailed;

  // Prepare data for pie chart based on selected view
  const pieChartData = pieChartView === 'jobs' ? [
    { name: 'Successful', value: jobsOverviewData.jobsStatusCount.numberOfJobsPassed, color: '#24B291' },
    { name: 'Failed', value: jobsOverviewData.jobsStatusCount.numberOfJobsFailed, color: '#F2520A' },
  ] : [
    { name: 'Successful', value: jobsOverviewData.iterationsStatusCount.numberOfIterationsPassed, color: '#24B291' },
    { name: 'Failed', value: jobsOverviewData.iterationsStatusCount.numberOfIterationsFailed, color: '#F2520A' },
  ];

  // Calculate total for pie chart percentages based only on displayed data
  const pieChartTotal = pieChartData.reduce((sum, item) => sum + item.value, 0);

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontWeight="bold">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Render logic for the Jobs Execution Overview section
  const jobsMetricData = {
    'Number of jobs executed': totalExecutedJobs,
    'Total test cases executed': jobsOverviewData.totalIterationJobs || 0,
  };

  // Top metrics data for the prominent section
  const topMetricsData = {
    'Number of Jobs': {
      icon: '📊',
      title: 'Number of Jobs',
      value: jobsOverviewData.totalJobsAvailable,
      subtitle: 'Total Jobs Available'
    },
    'Number of Test Suites': {
      icon: '🧪',
      title: 'Number of Test Suites',
      value: jobsOverviewData.numberOfTestSuites,
      subtitle: 'Total Test Suites Available'
    }
  };








  const filterTags = [
    'Filter name',
    'Filter name',
    'Filter name',
    'Filter name',
    'Filter name'
  ];

  // Transform real API data for rendering
  const topFailuresData = topFailures.map((failure, index) => ({
    id: index + 1,
    message: failure.failureMessage || 'No failure message available',
    count: failure.occurrenceCount || 0,
    executionId: failure.executionId,
    transactionId: failure.transactionId
  }));

  const topFailingJobsData = topFailingJobs.map((job, index) => ({
    id: index + 1,
    jobName: job.jobName || 'No job name available',
    failedCount: job.failureCount || 0,
    totalExecuted: job.totalCount || 0,
    executionId: job.executionId,
    transactionId: job.transactionId
  }));

  const handleBarChartClick = (chartData) => {
    if (!chartData || !chartData.activePayload || !chartData.activePayload.length) {
      return;
    }
    const data = chartData.activePayload[0].payload;

    if (data.executionId) {
      const reportUrl = `/execution/hosting/${data.executionId}`;
      window.open(reportUrl, '_blank');
    } else {
      alert('No execution ID available for this run.');
    }
  };

  // Custom Tooltip for Detailed Analysis Chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      // Do not render a tooltip for the gap elements
      if (data.gap) {
        return null;
      }

      return (
        <div className="custom-tooltip">
          <p className="tooltip-header"><strong>{data.run}</strong></p>
          <p>{`Date: ${data.fullDate}`}</p>
          <p style={{ color: '#24B291' }}>{`Passed: ${data.successful}`}</p>
          <p style={{ color: '#F2520A' }}>{`Failed: ${data.failed}`}</p>
          <p>{`Tool: ${data.tools || 'N/A'}`}</p>
          <div className="tooltip-footer">
            <p>Click to view detailed report</p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom Tooltip for Iteration Details Chart
  const CustomIterationTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      // Do not render a tooltip for the gap elements
      if (data.gap) {
        return null;
      }

      return (
        <div className="custom-tooltip">
          <p className="tooltip-header"><strong>{data.run}</strong></p>
          <p>{`Date: ${data.fullDate}`}</p>
          <p style={{ fontWeight: 'bold', marginTop: '8px' }}>{`Total Test Cases Executed: ${data.totalIterations}`}</p>
          <p style={{ color: '#24B291' }}>{`Test Cases Passed: ${data.successIterations}`}</p>
          <p style={{ color: '#F2520A' }}>{`Test Cases Failed: ${data.failedIterations}`}</p>
          <p style={{ color: '#FFA500' }}>{`Test Cases In Progress: ${data.inProgressIterations}`}</p>
          <p style={{ color: '#9CA3AF' }}>{`Test Cases Skipped: ${data.skippedIterations}`}</p>
          <p style={{ marginTop: '8px' }}>{`Tool: ${data.tools || 'N/A'}`}</p>
          <div className="tooltip-footer">
            <p>Click to view detailed report</p>
          </div>
        </div>
      );
    }
    return null;
  };

  const handleFailureClick = (failure) => {
    const org = appliedFilters.org;
    const { executionId, transactionId } = failure;

    if (org && executionId && transactionId) {
      const reportUrl = `/view/${org}/${executionId}?transactionid=${transactionId}`;
      window.open(reportUrl, '_blank');
    } else {
      alert('Missing data needed to generate the report link.');
    }
  };

  const handleFailingJobClick = (job) => {
    const org = appliedFilters.org;
    const { executionId, transactionId } = job;

    if (org && executionId && transactionId) {
      const reportUrl = `/view/${org}/${executionId}?transactionid=${transactionId}`;
      window.open(reportUrl, '_blank');
    } else {
      alert('Missing data needed to generate the report link.');
    }
  };

  // Pagination Logic for Top Failures
  const indexOfLastFailure = currentFailuresPage * itemsPerPage;
  const indexOfFirstFailure = indexOfLastFailure - itemsPerPage;
  const currentFailures = topFailuresData.slice(indexOfFirstFailure, indexOfLastFailure);
  const totalFailuresPages = Math.ceil(topFailuresData.length / itemsPerPage);

  // Pagination Logic for Top Failing Jobs
  const indexOfLastFailingJob = currentFailingJobsPage * itemsPerPage;
  const indexOfFirstFailingJob = indexOfLastFailingJob - itemsPerPage;
  const currentFailingJobs = topFailingJobsData.slice(indexOfFirstFailingJob, indexOfLastFailingJob);
  const totalFailingJobsPages = Math.ceil(topFailingJobsData.length / itemsPerPage);

  const handlePageChange = (pageSetter, newPage, totalPages) => {
    if (newPage > 0 && newPage <= totalPages) {
      pageSetter(newPage);
    }
  };

  // Get current chart data based on selected test suite
  const handleTestSuiteClick = (suiteName) => {
    setSelectedTestSuite(suiteName);
  };

  // Filter Functions
  // currentFilters is computed and defined above

  const openFiltersPanel = () => {
    const copiedFilters = {
      org: appliedFilters.org,
      workstream: appliedFilters.workstream,
      projects: [...appliedFilters.projects],
      team: appliedFilters.team,
      dateRange: { ...appliedFilters.dateRange }
    };

    setDraftFilters(copiedFilters);
    setFiltersModified(false);
    setShowFilters(true);
    setDropdownKey(prev => prev + 1);
  };

  const revertDraftFilters = () => {
    const revertedFilters = {
      org: appliedFilters.org,
      workstream: appliedFilters.workstream,
      projects: [...appliedFilters.projects],
      team: appliedFilters.team,
      dateRange: { ...appliedFilters.dateRange }
    };

    setDraftFilters(revertedFilters);
    setFiltersModified(false);
    setProjectClearCounter(prev => prev + 1);
    setDropdownKey(prev => prev + 1);
  };

  // Utility functions from Dashboard.js
  const getProjectNameForAPI = (projects) => {
    if (!projects || projects.length === 0) return '';

    // Map display names to project names using projectMapping
    const projectNames = projects.map(displayName => {
      const projectName = projectMapping[displayName];
      if (!projectName) {
        console.warn(`📁 No mapping found for project display name: "${displayName}"`);
        return displayName; // Fallback to display name
      }
      return projectName;
    });

    return projectNames.join(',');
  };

  const getProjectCacheKey = (projects) => {
    if (!projects || projects.length === 0) return 'no-projects';
    return projects.sort().join(',');
  };

  const createDashboardData = (jobNumbers, numberOfJobs, numberOfTestSuites) => {
    // Always return data structure, even if jobNumbers is null (show placeholders)
    return {
      totalJobsAvailable: parseInt(numberOfJobs?.total_jobs, 10) || 0,
      numberOfTestSuites: parseInt(numberOfTestSuites?.total_testsuites, 10) || 0,
      totalIterationJobs: jobNumbers ? parseInt(jobNumbers.total_iteration_jobs, 10) || 0 : 0,
      jobsStatusCount: {
        numberOfJobsPassed: jobNumbers ? parseInt(jobNumbers.success, 10) || 0 : 0,
        numberOfJobsFailed: jobNumbers ? parseInt(jobNumbers.failed, 10) || 0 : 0,
        numberOfJobsInProgress: jobNumbers ? parseInt(jobNumbers.in_progress, 10) || 0 : 0,
        numberOfJobsInQueue: jobNumbers ? parseInt(jobNumbers.queued, 10) || 0 : 0,
      },
      iterationsStatusCount: {
        numberOfIterationsPassed: jobNumbers ? parseInt(jobNumbers.iteration_success, 10) || 0 : 0,
        numberOfIterationsFailed: jobNumbers ? parseInt(jobNumbers.iteration_failed, 10) || 0 : 0,
      },
      // Add a flag to indicate if job execution data is still loading
      isJobExecutionDataLoading: !jobNumbers
    };
  };

  // API Loading Functions
  const loadNavigationData = async () => {
    if (navigationDataLoaded) {
      console.log('📊 [NAV DATA] Already loaded, skipping');
      return; // Prevent loading multiple times
    }

    // Prevent duplicate calls
    if (navigationLoadingRef.current) {
      console.log('📊 [NAV DATA] Already loading, skipping duplicate call');
      return;
    }

    console.log('📊 [NAV DATA] Starting navigation data load');
    navigationLoadingRef.current = true;

    // Use Redux store data ONLY - PrivateRoute ensures this is loaded before Dashboard renders
    const navigation = categories;

    if (!navigation || !navigation.projects || navigation.projects.length === 0) {
      console.log('⚠️ [NAV DATA] Redux navigation data not ready yet, will retry when available');
      // Do not make API calls - wait for Redux data from PrivateRoute
      navigationLoadingRef.current = false;
      return;
    }

    console.log('✅ [NAV DATA] Using Redux navigation data:', navigation.projects.length, 'projects');

    // Process navigation data (both Redux and API paths)
    setNavigationData(navigation);

    if (navigation && navigation.projects && navigation.projects.length > 0) {
        // Extract unique organizations (matching original Dashboard.js structure)
        const uniqueOrgs = [...new Set(navigation.projects.map(project => project.org))]
          .map(org => ({ sku: org, name: org.charAt(0).toUpperCase() + org.slice(1) }));

        // Extract all workstreams with organization info
        const workstreamsWithOrg = navigation.projects.flatMap(project =>
          project.workstream.map(ws => ({
            sku: ws.workstreamname,
            name: ws.displayname,
            orgName: project.org
          }))
        );

        // Extract all teams with workstream info
        const teamsWithWorkstream = navigation.projects.flatMap(project =>
          project.workstream.flatMap(ws =>
            (ws.teamnames || []).map(team => ({
              sku: team,
              name: team,
              workstreamName: ws.workstreamname,
              orgName: project.org
            }))
          )
        );

        // Update state with extracted data
        setOrgOptions(uniqueOrgs);
        setAllWorkstreams(workstreamsWithOrg);
        setAllTeams(teamsWithWorkstream);

        console.log('✅ Navigation data loaded successfully');
        console.log('📊 Organizations:', uniqueOrgs.length);
        console.log('📊 Workstreams:', workstreamsWithOrg.length);
        console.log('📊 Teams:', teamsWithWorkstream.length);
      } else {
        console.warn('⚠️ No projects found in navigation data');
        setOrgOptions([]);
        setAllWorkstreams([]);
        setAllTeams([]);
      }

    setNavigationDataLoaded(true);
    navigationLoadingRef.current = false;
  };

  const loadHostingProjects = async (org, workstream) => {
    if (!org || !workstream) {
      setProjectOptions([]);
      setProjectMapping({});
      setProjectMappingReady(false);
      return;
    }

    const loadingKey = `${org}-${workstream}`;
    
    // Prevent duplicate calls for the same org-workstream combination
    if (projectLoadingRef.current.has(loadingKey)) {
      console.log(`📁 [PROJECTS] Already loading projects for ${loadingKey}, skipping duplicate call`);
      return;
    }

    const cacheKey = `hosting-projects-${org}-${workstream}`;

    // Check if we already have this data in cache
    if (projectCache[cacheKey]) {
      console.log(`📦 Using cached hosting projects for ${org}/${workstream}`);
      setProjectOptions(projectCache[cacheKey].options);
      setProjectMapping(projectCache[cacheKey].mapping);
      setProjectMappingReady(true);

      // Auto-select first project when filter panel is open and none selected yet
      if (draftFilters && showFilters && projectCache[cacheKey].options.length > 0 && draftFilters.projects.length === 0) {
        const firstProject = projectCache[cacheKey].options[0];
        const newFilters = { ...draftFilters, projects: [firstProject] };
        setDraftFilters(newFilters);
        console.log(`📁 Auto-selected project: ${firstProject} (from cache)`);
      }
      return;
    }

    projectLoadingRef.current.add(loadingKey);
    setIsLoadingProjects(true);
    try {
      console.log(`🌐 Loading hosting projects for ${org}/${workstream}...`);
      const response = await getHostingProjects(org, workstream);
      // Support both response shapes: { project: [...] } (legacy Dashboard) and { data: [...] } (alt)
      const projects = (response?.data?.project || response?.data?.data || []);

      // Transform to display format and create mapping
      const projectOptions = projects.map(project => project.displayname).filter(Boolean);
      const mapping = {};
      projects.forEach(project => {
        if (project.displayname && project.projectname) {
          mapping[project.displayname] = project.projectname;
        }
      });

      // Cache the results
      setProjectCache(prev => ({
        ...prev,
        [cacheKey]: { options: projectOptions, mapping }
      }));

      setProjectOptions(projectOptions);
      setProjectMapping(mapping);
      setProjectMappingReady(true);

      // Auto-select first project when filter panel is open and none selected yet
      if (draftFilters && showFilters && projectOptions.length > 0 && draftFilters.projects.length === 0) {
        const firstProject = projectOptions[0];
        const newFilters = { ...draftFilters, projects: [firstProject] };
        setDraftFilters(newFilters);
        console.log(`📁 Auto-selected project: ${firstProject} (from API)`);
      }

      console.log(`✅ Loaded ${projectOptions.length} hosting projects for ${org}/${workstream}`);
    } catch (error) {
      console.error(`❌ Error loading hosting projects for ${org}/${workstream}:`, error);
      setProjectOptions([]);
      setProjectMapping({});
      setProjectMappingReady(false);
    } finally {
      setIsLoadingProjects(false);
      projectLoadingRef.current.delete(loadingKey);
    }
  };

  const toggleFilters = () => {
    if (showFilters) {
      if (filtersModified) {
        revertDraftFilters();
        setShowFilters(false);
      } else {
        setShowFilters(false);
        setDraftFilters(null);
      }
    } else {
      openFiltersPanel();
    }
  };


  // Remaining API Loading Functions
  const loadJobExecutionNumbers = async (org, workstream, teamName, dateRange = null, projects = [], setLoading = true) => {
    if (!org) {
      return null;
    }

    if (setLoading) {
      setIsLoadingDashboardData(true);
    }

    try {
      const { startDate, endDate } = getDateRange(dateRange);
      const project = getProjectNameForAPI(projects);

      const cacheKey = `dashboard-job-numbers-${org}-${workstream || 'no-workstream'}-${teamName || 'no-team'}-${project}-${startDate}-${endDate}`;
      const cacheTimeKey = `dashboard-job-numbers-${org}-${workstream || 'no-workstream'}-${teamName || 'no-team'}-${project}-${startDate}-${endDate}-time`;
      const cacheValidDuration = 5 * 60 * 1000; // 5 minutes

      const cachedData = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && cacheTime && (Date.now() - parseInt(cacheTime)) < cacheValidDuration) {
        console.log(`📦 Loading job numbers from cache`);
        return JSON.parse(cachedData);
      }

      console.log(`🌐 Fetching job numbers from API`);
      const response = await getNumberOfJobExecuted(teamName, startDate, endDate, null, org, workstream, project);
      const jobData = response.data.data && response.data.data.length > 0 ? response.data.data[0] : null;

      if (jobData) {
        localStorage.setItem(cacheKey, JSON.stringify(jobData));
        localStorage.setItem(cacheTimeKey, Date.now().toString());
        console.log(`🌐 Cached job numbers data`);
      }

      return jobData;
    } catch (error) {
      console.error('Error loading job execution numbers:', error);
      return null;
    } finally {
      if (setLoading) {
        setIsLoadingDashboardData(false);
      }
    }
  };

  const loadNumberOfJobs = async (org, workstream, projects = []) => {
    if (!org) return null;

    try {
      const project = getProjectNameForAPI(projects);
      const cacheKey = `dashboard-number-of-jobs-${org}-${workstream || 'no-workstream'}-${project}`;
      const cacheTimeKey = `${cacheKey}-time`;
      const cacheValidDuration = 10 * 60 * 1000;

      const cachedData = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && cacheTime && (Date.now() - parseInt(cacheTime)) < cacheValidDuration) {
        console.log(`📦 Loading number of jobs from cache`);
        return JSON.parse(cachedData);
      }

      console.log(`🌐 Fetching number of jobs from API`);
      const response = await getNumberOfJobs(org, workstream, project);
      const jobsData = response.data.data && response.data.data.length > 0 ? response.data.data[0] : null;

      if (jobsData) {
        localStorage.setItem(cacheKey, JSON.stringify(jobsData));
        localStorage.setItem(cacheTimeKey, Date.now().toString());
      }
      return jobsData;
    } catch (error) {
      console.error('Error loading number of jobs:', error);
      return null;
    }
  };

  const loadNumberOfTestSuites = async (org, workstream, projects = []) => {
    if (!org) return null;

    try {
      const project = getProjectNameForAPI(projects);
      const cacheKey = `dashboard-number-of-test-suites-${org}-${workstream || 'no-workstream'}-${project}`;
      const cacheTimeKey = `${cacheKey}-time`;
      const cacheValidDuration = 10 * 60 * 1000;

      const cachedData = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && cacheTime && (Date.now() - parseInt(cacheTime)) < cacheValidDuration) {
        console.log(`📦 Loading number of test suites from cache`);
        return JSON.parse(cachedData);
      }

      console.log(`🌐 Fetching number of test suites from API`);
      const response = await getNumberOfTestSuites(org, workstream, project);
      const suitesData = response.data.data && response.data.data.length > 0 ? response.data.data[0] : null;

      if (suitesData) {
        localStorage.setItem(cacheKey, JSON.stringify(suitesData));
        localStorage.setItem(cacheTimeKey, Date.now().toString());
      }
      return suitesData;
    } catch (error) {
      console.error('Error loading number of test suites:', error);
      return null;
    }
  };

  const loadTopFailures = async (org, workstream, teamName, dateRange = null, projects = []) => {
    if (!org) {
      setTopFailures([]);
      return;
    }

    setIsLoadingTopFailures(true);
    try {
      const { startDate, endDate } = getDateRange(dateRange);
      const project = getProjectNameForAPI(projects);

      const cacheKey = `dashboard-top-failures-${org}-${workstream || 'no-workstream'}-${teamName || 'no-team'}-${project}-${startDate}-${endDate}`;
      const cacheTimeKey = `${cacheKey}-time`;
      const cacheValidDuration = 5 * 60 * 1000; // 5 minutes

      const cachedData = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && cacheTime && (Date.now() - parseInt(cacheTime)) < cacheValidDuration) {
        console.log(`📦 Loading top failures from cache`);
        setTopFailures(JSON.parse(cachedData));
        return;
      }

      console.log(`🌐 Fetching top failures from API`);
      const response = await getTopMostFailures(teamName, startDate, endDate, TOP_FAILURES_API_LIMIT, null, org, workstream, project);
      const failures = response.data.data || [];

      const transformedFailures = failures.map(failure => ({
        failureMessage: failure.failure_message,
        occurrenceCount: parseInt(failure.occurrence_count),
        executionId: failure.sample_execution_id,
        transactionId: failure.sample_transaction_id,
        originalData: failure
      }));

      // Try to cache the data, but don't let cache failures prevent state updates
      try {
        localStorage.setItem(cacheKey, JSON.stringify(transformedFailures));
        localStorage.setItem(cacheTimeKey, Date.now().toString());
      } catch (cacheError) {
        console.warn('⚠️ [CACHE] Failed to cache top failures data:', cacheError.message);
        // Clear some old cache entries to free up space
        clearOldCacheEntries('dashboard-top-failures');
      }

      setTopFailures(transformedFailures);
      console.log('✅ [TOP_FAILURES] State updated with', transformedFailures.length, 'failures');
    } catch (error) {
      console.error('Error loading top failures:', error);
      setTopFailures([]);
    } finally {
      setIsLoadingTopFailures(false);
    }
  };

  const loadTopFailingJobs = async (org, workstream, teamName, dateRange = null, projects = []) => {
    if (!org) {
      setTopFailingJobs([]);
      return;
    }

    setIsLoadingTopFailingJobs(true);
    try {
      const { startDate, endDate } = getDateRange(dateRange);
      const project = getProjectNameForAPI(projects);

      const cacheKey = `dashboard-top-failing-jobs-${org}-${workstream || 'no-workstream'}-${teamName || 'no-team'}-${project}-${startDate}-${endDate}`;
      const cacheTimeKey = `${cacheKey}-time`;
      const cacheValidDuration = 5 * 60 * 1000; // 5 minutes

      const cachedData = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && cacheTime && (Date.now() - parseInt(cacheTime)) < cacheValidDuration) {
        console.log(`📦 Loading top failing jobs from cache`);
        setTopFailingJobs(JSON.parse(cachedData));
        return;
      }

      console.log(`🌐 Fetching top failing jobs from API`);
      const response = await getTopFailingJobs(teamName, startDate, endDate, TOP_FAILING_JOBS_API_LIMIT, null, org, workstream, project);
      const jobs = response.data.data || [];

      const transformedJobs = jobs.map(job => ({
        jobName: job.job_name,
        totalCount: parseInt(job.total_count),
        failureCount: parseInt(job.failure_count),
        executionId: job.sample_execution_id,
        transactionId: job.sample_transaction_id,
        originalData: job
      }));

      // Try to cache the data, but don't let cache failures prevent state updates
      try {
        localStorage.setItem(cacheKey, JSON.stringify(transformedJobs));
        localStorage.setItem(cacheTimeKey, Date.now().toString());
      } catch (cacheError) {
        console.warn('⚠️ [CACHE] Failed to cache top failing jobs data:', cacheError.message);
        // Clear some old cache entries to free up space
        clearOldCacheEntries('dashboard-top-failing-jobs');
      }

      setTopFailingJobs(transformedJobs);
      console.log('✅ [TOP_FAILING_JOBS] State updated with', transformedJobs.length, 'jobs');
    } catch (error) {
      console.error('Error loading top failing jobs:', error);
      setTopFailingJobs([]);
    } finally {
      setIsLoadingTopFailingJobs(false);
    }
  };

  const loadTestsuiteExecutionData = async (org, workstream, projects, dateRange) => {
    if (!org) {
      setApiSuiteData([]);
      setSuiteExecutionCounts([]);
      return;
    }

    setIsLoadingTestSuiteData(true);
    try {
      const { startDate, endDate } = getDateRange(dateRange);
      const projectName = getProjectNameForAPI(projects);
      const cacheKey = `dashboard-testsuite-executions-${org}-${workstream || 'no-workstream'}-${projectName}-${startDate}-${endDate}`;
      const cacheTimeKey = `${cacheKey}-time`;
      const cacheValidDuration = 5 * 60 * 1000; // 5 minutes

      const cachedData = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && cacheTime && Date.now() - parseInt(cacheTime) < cacheValidDuration) {
        const suiteData = JSON.parse(cachedData);
        setApiSuiteData(suiteData);
        // Process and set execution counts from cache
        const counts = suiteData.map(suite => ({
          name: suite.testsuitename,
          count: Object.values(suite.DayWiseExecutions).flat().length
        })).sort((a, b) => b.count - a.count);
        setSuiteExecutionCounts(counts);
        console.log('📦 Using cached test suite data.');
        return;
      }

      console.log('🌐 Fetching fresh test suite data from API.');
      const response = await getTestsuiteExecutions(org, workstream, projectName, startDate, endDate);
      const suiteData = response.data.data || [];
      
      console.log('📊 [API_RESPONSE] Test suite execution data received:', {
        totalSuites: suiteData.length,
        dateRange: `${startDate} to ${endDate}`,
        suiteNames: suiteData.map(s => s.testsuitename),
        dataStructure: suiteData.length > 0 ? {
          sampleSuite: suiteData[0].testsuitename,
          dayWiseExecutionsKeys: Object.keys(suiteData[0].DayWiseExecutions || {}),
          totalDaysInFirstSuite: Object.keys(suiteData[0].DayWiseExecutions || {}).length
        } : 'No suites'
      });

      // Try to cache the data, but don't let cache failures prevent state updates
      try {
        localStorage.setItem(cacheKey, JSON.stringify(suiteData));
        localStorage.setItem(cacheTimeKey, Date.now().toString());
      } catch (cacheError) {
        console.warn('⚠️ [CACHE] Failed to cache test suite data:', cacheError.message);
        // Clear some old cache entries to free up space
        clearOldCacheEntries('dashboard-testsuite-executions');
      }

      setApiSuiteData(suiteData);

      // Process and set execution counts from API response
      const counts = suiteData.map(suite => ({
        name: suite.testsuitename,
        count: Object.values(suite.DayWiseExecutions).flat().length
      })).sort((a, b) => b.count - a.count);
      setSuiteExecutionCounts(counts);
      console.log('✅ [TEST_SUITE] State updated with', suiteData.length, 'suites and', counts.length, 'execution counts');

    } catch (error) {
      console.error('Error fetching test suite execution data:', error);
      setApiSuiteData([]);
      setSuiteExecutionCounts([]);
    } finally {
      setIsLoadingTestSuiteData(false);
    }
  };

  const loadExecutionIterationDetails = async (org, workstream, projects, dateRange) => {
    if (!org) {
      setApiIterationData([]);
      setIterationChartData([]);
      return;
    }

    setIsLoadingIterationDetails(true);
    try {
      const { startDate, endDate } = getDateRange(dateRange);
      const projectName = getProjectNameForAPI(projects);
      const cacheKey = `dashboard-iteration-details-${org}-${workstream || 'no-workstream'}-${projectName}-${startDate}-${endDate}`;
      const cacheTimeKey = `${cacheKey}-time`;
      const cacheValidDuration = 5 * 60 * 1000; // 5 minutes

      const cachedData = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && cacheTime && Date.now() - parseInt(cacheTime) < cacheValidDuration) {
        const iterationData = JSON.parse(cachedData);
        setApiIterationData(iterationData);
        console.log('📦 Using cached iteration details data.');
        return;
      }

      console.log('🌐 Fetching fresh iteration details data from API.');
      const response = await getExecutionIterationDetails(org, workstream, projectName, startDate, endDate);
      const iterationData = response.data.data || [];
      
      console.log('📊 [API_RESPONSE] Iteration details data received:', {
        totalSuites: iterationData.length,
        dateRange: `${startDate} to ${endDate}`,
        suiteNames: iterationData.map(s => s.testsuitename)
      });

      // Try to cache the data, but don't let cache failures prevent state updates
      try {
        localStorage.setItem(cacheKey, JSON.stringify(iterationData));
        localStorage.setItem(cacheTimeKey, Date.now().toString());
      } catch (cacheError) {
        console.warn('⚠️ [CACHE] Failed to cache iteration details data:', cacheError.message);
        clearOldCacheEntries('dashboard-iteration-details');
      }

      setApiIterationData(iterationData);
      console.log('✅ [ITERATION_DETAILS] State updated with', iterationData.length, 'suites');

    } catch (error) {
      console.error('Error fetching iteration details data:', error);
      setApiIterationData([]);
      setIterationChartData([]);
    } finally {
      setIsLoadingIterationDetails(false);
    }
  };

  const applyFilters = async () => {
    if (!draftFilters.org) {
      alert('Please select an organization');
      return;
    }

    // Reset data and show loader
    setIsDashboardLoading(true);
    setDashboardData(null);
    setTopFailures([]);  // Initialize to empty array instead of null
    setTopFailingJobs([]);  // Initialize to empty array instead of null
    setApiSuiteData(null);
    setSuiteExecutionCounts([]);
    setDrilldownChartData([]);

    const newAppliedFilters = { ...draftFilters };
    setAppliedFilters(newAppliedFilters);
    setFiltersModified(false);
    setShowFilters(false);
    setDraftFilters(null); // Clear draft filters after applying

    // Update URL with new filters
    updateUrlWithFilters(newAppliedFilters);

    // Create tags from applied filters
    const newTags = generateTagsFromFilters(newAppliedFilters);
    setActiveFilterTags(newTags);
    console.log('✅ Filters applied successfully:', newAppliedFilters);
  };

  const handleRemoveTag = (tagToRemove) => {
    const { type, value } = tagToRemove;
    const newFilters = { ...appliedFilters };

    if (type === 'Project') {
      newFilters.projects = []; // Clear all projects
    } else {
      const key = type.toLowerCase();
      newFilters[key] = '';
      if (key === 'org') {
        newFilters.workstream = '';
        newFilters.team = '';
        newFilters.projects = [];
      } else if (key === 'workstream') {
        newFilters.team = '';
        newFilters.projects = [];
      }
    }

    setAppliedFilters(newFilters);

    // Update URL with new filters
    updateUrlWithFilters(newFilters);

    // Regenerate tags from the new filters
    const newTags = generateTagsFromFilters(newFilters);
    setActiveFilterTags(newTags);

    // Also update draft if panel is open
    if (showFilters) {
      setDraftFilters(newFilters);
    }
  };

  const generateTagsFromFilters = (filters) => {
    const tags = [];
    if (filters.org) {
      // For org, capitalize first letter if no mapping found
      const orgOption = orgOptions.find(o => o.sku === filters.org);
      const orgDisplayName = orgOption ? orgOption.name :
        filters.org.charAt(0).toUpperCase() + filters.org.slice(1);
      tags.push({ type: 'Org', value: orgDisplayName });
    }
    if (filters.workstream) {
      // For workstream, find the display name from allWorkstreams
      const workstreamOption = allWorkstreams.find(w => w.sku === filters.workstream && w.orgName === filters.org);
      const workstreamDisplayName = workstreamOption ? workstreamOption.name :
        filters.workstream.charAt(0).toUpperCase() + filters.workstream.slice(1);
      tags.push({ type: 'Workstream', value: workstreamDisplayName });
    }
    if (filters.team) {
      // For team, find the display name from allTeams
      const teamOption = allTeams.find(t => t.sku === filters.team && t.workstreamName === filters.workstream);
      const teamDisplayName = teamOption ? teamOption.name : filters.team;
      tags.push({ type: 'Team', value: teamDisplayName });
    }
    if (filters.projects && filters.projects.length > 0) {
      // Projects are already in display name format
      tags.push({ type: 'Project', value: filters.projects.join(', ') });
    }
    return tags;
  };

  const clearFilters = () => {
    const clearedFilters = {
      org: '', workstream: '', projects: [], team: '',
      dateRange: getDateRange() // Reset to default last 7 days
    };
    setDraftFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setActiveFilterTags([]);
    setFiltersModified(false);

    // Update URL to clear parameters
    updateUrlWithFilters(clearedFilters);
  };

  const handleOrgChange = (value) => {
    if (!draftFilters) return; // Only handle changes when filter panel is initialized

    // Auto-select first available workstream and team for the selected org (mimics legacy Dashboard behaviour)
    const availableWorkstreams = allWorkstreams.filter(ws => ws.orgName === value);
    const firstWorkstream = availableWorkstreams.length > 0 ? availableWorkstreams[0].sku : '';

    const availableTeams = allTeams.filter(team => team.workstreamName === firstWorkstream);
    const firstTeam = availableTeams.length > 0 ? availableTeams[0].sku : '';

    const newFilters = {
      ...draftFilters,
      org: value,
      workstream: firstWorkstream,
      team: firstTeam,
      projects: [] // Reset projects when org changes
    };

    setDraftFilters(newFilters);
    setProjectClearCounter(prev => prev + 1); // Clear any previously selected projects
    setDropdownKey(prev => prev + 1); // Force dropdowns to refresh
    setFiltersModified(true);
  };

  const handleWorkstreamChange = (value) => {
    if (!draftFilters) return; // Ensure panel initialized before handling

    // Auto-select the first available team for the selected workstream
    const availableTeams = allTeams.filter(team => team.workstreamName === value);
    const firstTeam = availableTeams.length > 0 ? availableTeams[0].sku : '';

    const newFilters = {
      ...draftFilters,
      workstream: value,
      team: firstTeam,
      projects: [] // Reset projects when workstream changes
    };

    setDraftFilters(newFilters);
    setProjectClearCounter(prev => prev + 1);
    setDropdownKey(prev => prev + 1);
    setFiltersModified(true);
  };

  const handleProjectsChange = (event) => {
    if (draftFilters) {
      const newFilters = {
        ...draftFilters,
        projects: event.target.value
      };
      setDraftFilters(newFilters);
      setFiltersModified(true);
    }
  };

  const handleTeamChange = (value) => {
    if (draftFilters) {
      const newFilters = {
        ...draftFilters,
        team: value
      };
      setDraftFilters(newFilters);
      setFiltersModified(true);
    }
  };

  const handleDateRangeChange = (field, value) => {
    if (draftFilters) {
      // Validate date is within allowed range (only if value is provided)
      if (value) {
        const selectedDate = new Date(value + 'T00:00:00'); // Add time to avoid timezone issues
        const today = new Date();
        today.setHours(23, 59, 59, 999); // Set to end of today
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        threeMonthsAgo.setHours(0, 0, 0, 0); // Set to start of day
        
        // Check if date is within valid range
        if (selectedDate > today) {
          console.warn('Future dates are not allowed:', value);
          return;
        }
        
        if (selectedDate < threeMonthsAgo) {
          console.warn('Dates older than 3 months are not allowed:', value);
          return;
        }
      }

      const newFilters = {
        ...draftFilters,
        dateRange: {
          ...draftFilters.dateRange,
          [field]: value
        }
      };
      setDraftFilters(newFilters);
      setFiltersModified(true);
    }
  };

  const clearOrganization = () => handleOrgChange('');
  const clearWorkstream = () => handleWorkstreamChange('');
  const clearTeam = () => handleTeamChange('');
  const clearProjects = () => {
    if (draftFilters) {
      const newFilters = { ...draftFilters, projects: [] };
      setDraftFilters(newFilters);
      setFiltersModified(true);
      setProjectClearCounter(prev => prev + 1);
    }
  };

  // Calculate date restrictions for date inputs
  const getDateRestrictions = () => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    
    return {
      minDate: threeMonthsAgo.toISOString().split('T')[0],
      maxDate: today.toISOString().split('T')[0]
    };
  };

  const { minDate, maxDate } = getDateRestrictions();

  // Log dashboard component mount time and clean up localStorage if needed
  useEffect(() => {
    const mountTime = performance.now() - dashboardStartTime.current;
    console.log(`📊 [PERFORMANCE] DashboardV2 mounted in ${mountTime.toFixed(2)}ms`);
    //console.log(`🔧 [CONFIG] API Mode: ${USE_UNIFIED_API ? 'Unified API (with fallback)' : 'Individual APIs only'}`);
    
    // Check localStorage usage and clean up if needed
    try {
      const usage = JSON.stringify(localStorage).length;
      const maxSize = 5 * 1024 * 1024; // 5MB typical limit
      if (usage > maxSize * 0.8) { // If over 80% full
        console.warn(`⚠️ [CACHE] localStorage is ${Math.round(usage/1024/1024)}MB, cleaning up old entries...`);
        clearOldCacheEntries('dashboard-');
      }
    } catch (error) {
      console.warn('⚠️ [CACHE] Could not check localStorage usage:', error);
    }
  }, []);

  // Load navigation data on component mount
  useEffect(() => {
    // Only show loading if we have URL parameters or it's a fresh load
    if (location.search || !hasInitializedRef.current) {
      setIsDashboardLoading(true);
      console.log('🚀 DashboardV2 mounted - starting navigation data load...');
      loadNavigationData();
      hasInitializedRef.current = true;
    }
  }, [location.search]);

  // Watch for categories changes from Redux to trigger navigation data loading
  useEffect(() => {
    // Load navigation data when categories become available from PrivateRoute
    if (categories && categories.projects && categories.projects.length > 0 && !navigationDataLoaded) {
      console.log('🔄 [NAV DATA] Categories available from Redux, loading navigation data...');
      loadNavigationData();
    }
  }, [categories, navigationDataLoaded]);

  // Debug effect to log dropdown data
  useEffect(() => {
    console.log('🔍 [DEBUG] Current dropdown data:', {
      orgOptions: orgOptions.length,
      allWorkstreams: allWorkstreams.length,
      allTeams: allTeams.length,
      projectOptions: projectOptions.length,
      navigationDataLoaded
    });
  }, [orgOptions.length, allWorkstreams.length, allTeams.length, projectOptions.length, navigationDataLoaded]);

  // Load hosting projects when org/workstream changes
  useEffect(() => {
    if (currentFilters.org && currentFilters.workstream) {
      loadHostingProjects(currentFilters.org, currentFilters.workstream);
    }
  }, [currentFilters.org, currentFilters.workstream]);

  // Effect to control loading indicator visibility
  useEffect(() => {
    if (isDataLoaded && loadingProgress === 100) {
      const hideTime = performance.now();
      console.log(`⚡ [TIMING] Hiding loading indicator at ${(hideTime - dashboardStartTime.current).toFixed(0)}ms`);
      // Hide loading immediately for faster UX - no artificial delay needed
      setIsDashboardLoading(false);
      setLoadingProgress(0);
      setIsDataLoaded(false);
      console.log('⚡ Loading complete, hiding indicator immediately');
    }
  }, [isDataLoaded, loadingProgress]);

  // Main dashboard data loading effect
  useEffect(() => {
    const hasOrg = !!appliedFilters.org;
    const hasProjectsInFilters = appliedFilters.projects.length > 0;

    // If projects are selected, we must wait for the mapping to be ready.
    // If no projects are selected, we don't need to wait.
    const projectMappingIsReady = hasProjectsInFilters ? projectMappingReady : true;

    const canProceed = navigationDataLoaded && hasOrg && !isLoading && projectMappingIsReady;

    // Create unique parameters string to prevent duplicate calls with same parameters
    const currentParams = `${appliedFilters.org}-${appliedFilters.workstream}-${appliedFilters.team}-${appliedFilters.projects.join(',')}-${appliedFilters.dateRange.startDate}-${appliedFilters.dateRange.endDate}`;
    
    // Skip if we're already loading the same data
    if (canProceed && lastDataLoadParams.current === currentParams) {
      console.log('🔍 [ENHANCED] Skipping duplicate data load for same parameters:', currentParams);
      return;
    }

    console.log('🔍 [ENHANCED] Data loading conditions check:', {
      timestamp: new Date().toISOString(),
      navigationDataLoaded,
      hasOrg,
      isLoading,
      projectMappingReady,
      hasProjectsInFilters,
      projectMappingSize: Object.keys(projectMapping).length,
      projectMappingIsReady,
      canProceed,
      appliedFilters: appliedFilters,
      currentUrl: location.search,
      debugInfo: {
        'Navigation loaded': navigationDataLoaded ? '✅' : '❌',
        'Has org': hasOrg ? '✅' : `❌ (org: "${appliedFilters.org}")`,
        'Not loading': !isLoading ? '✅' : '❌ (still loading)',
        'Project mapping ready': projectMappingIsReady ? '✅' : '❌'
      }
    });

    if (!canProceed) {
      console.warn('⚠️ [ENHANCED] Data loading BLOCKED. Reasons:');
      if (!navigationDataLoaded) console.warn('  - Navigation data not loaded yet');
      if (!hasOrg) console.warn('  - No organization selected');
      if (isLoading) console.warn('  - Already loading');
      if (!projectMappingIsReady) console.warn('  - Project mapping not ready');
      return;
    }

    if (canProceed) {
      console.log(`🚀 [ENHANCED] Loading dashboard data: ${appliedFilters.org}/${appliedFilters.workstream || 'no-workstream'}/${appliedFilters.team || 'no-team'} (projects: ${appliedFilters.projects.length})`);

      // Track current parameters to prevent duplicates
      lastDataLoadParams.current = currentParams;

      // Show loading state
      setIsDashboardLoading(true);
      setLoadingProgress(0);

      const loadDashboardData = async () => {
        try {
          // Log when first API call starts
          const apiStartTime = performance.now() - dashboardStartTime.current;
          console.log(`🌐 [PERFORMANCE] First API call starting after ${apiStartTime.toFixed(2)}ms from dashboard mount`);
          
          setLoadingProgress(10);

          // Load data using individual API calls
          console.log('🔧 [CONFIG] Using individual APIs');
          setLoadingProgress(20);

          let jobNumbers = null;
          let numberOfJobs = null;
          let numberOfTestSuites = null;

          try {
            // Load only the 2 fastest/simplest critical APIs
            const results = await Promise.allSettled([
              loadNumberOfJobs(
                appliedFilters.org,
                appliedFilters.workstream,
                appliedFilters.projects
              ),
              loadNumberOfTestSuites(
                appliedFilters.org,
                appliedFilters.workstream,
                appliedFilters.projects
              )
            ]);

            numberOfJobs = results[0].status === 'fulfilled' ? results[0].value : null;
            numberOfTestSuites = results[1].status === 'fulfilled' ? results[1].value : null;

            if (!numberOfJobs) console.error('❌ Failed to load number of jobs:', results[0].reason);
            if (!numberOfTestSuites) console.error('❌ Failed to load number of test suites:', results[1].reason);

            // Hide loading bar after just 2 simple APIs complete (fastest response)
            const criticalDataTime = performance.now();
            console.log('✅ [LOADING] 2 Critical APIs loaded, hiding loading indicator...');
            console.log(`⚡ [TIMING] Loading hidden after ${(criticalDataTime - dashboardStartTime.current).toFixed(0)}ms`);
            setLoadingProgress(100);
            setIsDataLoaded(true);
            
          } catch (error) {
            console.error('❌ Failed to load critical number data:', error);
            // Still hide loading bar even if some calls failed
            setLoadingProgress(100);
            setIsDataLoaded(true);
          }

          // Update dashboard data with available numbers (jobNumbers will be null initially)
          const updatedDashboardData = createDashboardData(jobNumbers, numberOfJobs, numberOfTestSuites);
          setDashboardData(updatedDashboardData);
          console.log('✅ Basic dashboard data updated, loading additional data in background...');

          // Load job execution numbers and additional data in background (don't affect loading state)
          const dataLoadPromises = [
            loadJobExecutionNumbers(
              appliedFilters.org,
              appliedFilters.workstream,
              appliedFilters.team,
              appliedFilters.dateRange,
              appliedFilters.projects,
              false
            ).then(result => {
              if (result) {
                jobNumbers = result;
                // Update dashboard data again with job execution numbers
                const fullDashboardData = createDashboardData(jobNumbers, numberOfJobs, numberOfTestSuites);
                setDashboardData(fullDashboardData);
                console.log('✅ [BACKGROUND] Job execution numbers loaded and dashboard updated');
              }
            }).catch(err => console.error('Failed to load job execution numbers:', err)),

            loadTopFailures(
              appliedFilters.org,
              appliedFilters.workstream,
              appliedFilters.team,
              appliedFilters.dateRange,
              appliedFilters.projects
            ).then(() => {
              console.log('✅ [BACKGROUND] Top failures loaded successfully');
            }).catch(err => console.error('Failed to load top failures:', err)),

            loadTopFailingJobs(
              appliedFilters.org,
              appliedFilters.workstream,
              appliedFilters.team,
              appliedFilters.dateRange,
              appliedFilters.projects
            ).then(() => {
              console.log('✅ [BACKGROUND] Top failing jobs loaded successfully');
            }).catch(err => console.error('Failed to load top failing jobs:', err)),

            loadTestsuiteExecutionData(
              appliedFilters.org,
              appliedFilters.workstream,
              appliedFilters.projects,
              appliedFilters.dateRange
            ).then(() => {
              console.log('✅ [BACKGROUND] Test suite execution data loaded successfully');
            }).catch(err => console.error('Failed to load test suite execution data:', err)),

            loadExecutionIterationDetails(
              appliedFilters.org,
              appliedFilters.workstream,
              appliedFilters.projects,
              appliedFilters.dateRange
            ).then(() => {
              console.log('✅ [BACKGROUND] Iteration details data loaded successfully');
            }).catch(err => console.error('Failed to load iteration details data:', err))
          ];

          // Load additional data in background without affecting loading state
          Promise.allSettled(dataLoadPromises).then(() => {
            console.log('✅ [BACKGROUND] All additional dashboard data loaded');
          });

        } catch (error) {
          console.error('❌ Critical error loading dashboard data:', error);
          setDashboardData(null);
          // Hide loading indicator even on error
          setLoadingProgress(100);
          setIsDataLoaded(true);
        }
        // Note: Loading state is now managed within the try block for faster UX
      };

      loadDashboardData();
      setInitialDataLoaded(true);
    } else {
      // If navigation is loaded and we still don't have an org, then it's a valid final state.
      // We can hide the loader and show the empty dashboard.
      console.log('🧹 No org selected after navigation load, hiding loader.');
      setDashboardData(null);
      setTopFailures([]);
      setTopFailingJobs([]);
      setLoadingProgress(100);
      setIsDataLoaded(true);
    }
  }, [
    navigationDataLoaded,
    appliedFilters.org,
    appliedFilters.workstream,
    appliedFilters.team,
    appliedFilters.projects.join(','),
    appliedFilters.dateRange.startDate,
    appliedFilters.dateRange.endDate,
    projectMappingReady
  ]);

  // Load last refresh time on mount and set an interval to re-fetch every 5 minutes
  useEffect(() => {
    const fetchRefreshTime = async () => {
      try {
        const response = await getLastRefreshTime();
        if (response.data && response.data.data) {
          setLastRefreshTime(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching last refresh time:', error);
      }
    };

    fetchRefreshTime(); // Fetch immediately on mount
    const intervalId = setInterval(fetchRefreshTime, 5 * 60 * 1000); // Refresh every 5 minutes

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, []);

  // Parse URL parameters and auto-select filters on component mount
  useEffect(() => {
    // Reset initialization flag when URL changes
    hasInitializedRef.current = false;

    // Clear potentially stale cached data when URL changes to prevent wrong data display
    // This ensures fresh data loading when navigating via URL
    if (location.search) {
      console.log('🧹 [URL CHANGE] Clearing potentially stale cache data for URL navigation');
      // Note: We don't clear all cache, just reset loading states to force fresh data evaluation
      setInitialDataLoaded(false);
      setIsDashboardLoading(true);
    }

    // Wait for navigation data and dropdown options to be loaded
    if (!navigationDataLoaded || !orgOptions.length || !allWorkstreams.length) {
      console.log('⏳ Waiting for navigation data and dropdown options to load...');
      return;
    }

    const urlParams = new URLSearchParams(location.search);

    if (urlParams.toString()) {
      // Extract filters from URL
      const projectsParam = urlParams.get('projects');
      const urlFilters = {
        org: urlParams.get('org') || '',
        workstream: urlParams.get('workstream') || '',
        projects: projectsParam ? projectsParam.split(',').filter(p => p.trim()) : [],
        team: urlParams.get('team') || '',
        dateRange: {
          startDate: urlParams.get('startDate') || getDateRange().startDate,
          endDate: urlParams.get('endDate') || getDateRange().endDate
        }
      };

      console.log('🔗 [URL PARSING] Extracted filters from URL:', {
        org: urlFilters.org || '(empty)',
        workstream: urlFilters.workstream || '(empty)',
        projects: urlFilters.projects.length > 0 ? urlFilters.projects : '(empty)',
        team: urlFilters.team || '(empty)',
        dateRange: `${urlFilters.dateRange.startDate} to ${urlFilters.dateRange.endDate}`
      });

      // Validate org exists in options
      if (urlFilters.org && !orgOptions.find(o => o.sku === urlFilters.org)) {
        console.warn(`⚠️ Invalid org in URL: ${urlFilters.org}`);
        return;
      }

      // Validate workstream exists for org
      if (urlFilters.workstream) {
        const validWorkstream = allWorkstreams.find(w => w.sku === urlFilters.workstream && w.orgName === urlFilters.org);
        if (!validWorkstream) {
          console.warn(`⚠️ Invalid workstream in URL: ${urlFilters.workstream}`);
          return;
        }
      }

      // Apply URL filters immediately
      setAppliedFilters(urlFilters);

      // Generate and set filter tags
      const tags = generateTagsFromFilters(urlFilters);
      setActiveFilterTags(tags);
      console.log('🏷️ Setting filter tags from URL:', tags);

      // If there are meaningful filters, prepare for data loading
      if (urlFilters.org && !hasInitializedRef.current) {
        console.log('🔄 Loading data from URL...');
        setInitialDataLoaded(false);
        setIsDashboardLoading(true); // Show loading immediately for URL-based loads
        hasInitializedRef.current = true; // Prevent re-initialization
      }
    } else {
      // Auto-select first available options for fresh dashboard load
      const shouldAutoSelect = orgOptions.length > 0 && !appliedFilters.org;

      if (shouldAutoSelect) {
        console.log('✅ [AUTO-SELECT] Applying auto-selection for fresh dashboard load');

        const firstOrg = orgOptions[0].sku; // Get the sku property from the object
        const availableWorkstreams = allWorkstreams.filter(ws => ws.orgName === firstOrg);
        const firstWorkstream = availableWorkstreams.length > 0 ? availableWorkstreams[0].sku : '';
        const availableTeams = allTeams.filter(team => team.workstreamName === firstWorkstream);
        const firstTeam = availableTeams.length > 0 ? availableTeams[0].sku : '';

        const initialFilters = {
          org: firstOrg,
          workstream: firstWorkstream,
          projects: [],
          team: firstTeam,
          dateRange: getDateRange() // Default to last 7 days
        };

        console.log('🚀 [AUTO-SELECT] Setting initial filters:', initialFilters);
        setAppliedFilters(initialFilters);

        const tags = generateTagsFromFilters(initialFilters);
        setActiveFilterTags(tags);

        // Update URL with auto-selected filters
        updateUrlWithFilters(initialFilters);
      }
    }
  }, [navigationDataLoaded, orgOptions.length, allWorkstreams.length, allTeams.length, location.search]);

  // Update URL with current filters
  const updateUrlWithFilters = (filters) => {
    const params = new URLSearchParams();

    if (filters.org) params.set('org', filters.org);
    if (filters.workstream) params.set('workstream', filters.workstream);
    if (filters.team) params.set('team', filters.team);
    if (filters.projects.length > 0) params.set('projects', filters.projects.join(','));
    if (filters.dateRange.startDate) params.set('startDate', filters.dateRange.startDate);
    if (filters.dateRange.endDate) params.set('endDate', filters.dateRange.endDate);

    const newUrl = `${location.pathname}?${params.toString()}`;
    history.replace(newUrl);
  };

  return (
    <ThemeContext.Provider value={lightGrayMediumTheme}>
      <div className="dashboard-v2-weave">
        {isDashboardLoading && <LoadingIndicator progress={loadingProgress} />}
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Typography variant="body">
            <span className="breadcrumb-link">Home</span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Dashboard</span>
          </Typography>
        </div>

        {/* Header */}
        <div className="dashboard-header">
          <div className="header-controls">
            <div className="header-left">
              <Typography variant="h1" className="dashboard-title">Dashboard</Typography>
              <Typography variant="h2" className="welcome-message">Welcome back, {displayName || emailId}!</Typography>
              <Typography variant="body" className="description">
                Comprehensive testing analytics platform providing detailed job execution metrics and test suite execution trends.
              </Typography>
            </div>
          </div>
        </div>



        {/* Controls */}
        <div className="dashboard-controls">
          <Button variant="solid" className="filters-btn" onClick={toggleFilters}>
            <Filter24 />
            Filters
          </Button>
          <div className="last-refreshed">
            <Typography variant="body">
              <DataRefresh16 />
              Last refreshed: {formatRefreshTime(lastRefreshTime)}
            </Typography>
          </div>
        </div>

        <Spacer spacing="m" />

        {/* Filter Tags */}
        <div className="filter-tags">
          {activeFilterTags.map(tag => {
            const isOrg = tag.type === 'Org';
            const isWorkstream = tag.type === 'Workstream';
            const hasProjectsSelected = (appliedFilters.projects || []).length > 0;

            // Rules:
            // - Org: never removable from here
            // - Workstream: removable only when no projects are selected
            const shouldBlockRemoval = isOrg || (isWorkstream && hasProjectsSelected);

            const handleClick = () => {
              if (isOrg) {
                return; // Org cannot be removed here
              }
              if (isWorkstream && hasProjectsSelected) {
                // Show tooltip guidance instead of removing
                setWorkstreamTagTooltipVisible(true);
                if (workstreamTooltipTimerRef.current) {
                  clearTimeout(workstreamTooltipTimerRef.current);
                }
                workstreamTooltipTimerRef.current = setTimeout(() => {
                  setWorkstreamTagTooltipVisible(false);
                }, 2200);
                return;
              }
              handleRemoveTag(tag);
            };

            const titleText = shouldBlockRemoval
              ? (isOrg ? 'Organization cannot be cleared from here' : 'Remove Project tag first')
              : 'Remove';

            return (
              <div key={tag.type} className="filter-tag">
                <span>{`${tag.type}: ${tag.value}`}</span>
                <button
                  className={`remove-tag-btn ${shouldBlockRemoval ? 'disabled' : ''}`}
                  onClick={handleClick}
                  title={titleText}
                >
                  <CloseMUI />
                </button>
                {isWorkstream && hasProjectsSelected && workstreamTagTooltipVisible && (
                  <div className="tag-tooltip">Remove Project filter first to change Workstream</div>
                )}
              </div>
            );
          })}
        </div>

        <Spacer spacing="m" />

        {/* Top Metrics Section */}
        <div className="top-metrics-section">
          {Object.entries(topMetricsData).map(([key, data]) => (
            <Surface key={key} className="top-metric-card">
              <div className="top-metric-header">
                <Typography variant="h4" className="metric-header-title">
                  <span className="metric-header-icon">{data.icon}</span>
                  {data.title}
                </Typography>
              </div>
              <div className="top-metric-content">
                <Typography variant="h1" weight="bold" className="metric-value">
                  {data.value}
                </Typography>
                <Typography variant="caption" className="metric-subtitle">
                  {data.subtitle}
                </Typography>
              </div>
            </Surface>
          ))}
        </div>

        {/* Section Divider */}
        <div className="section-divider">
          <div className="divider-line"></div>
        </div>

        <Spacer spacing="m" />

        {/* Jobs Execution Section - Two cards side by side */}
        <div className="jobs-execution-section">
          {/* Jobs Execution Overview */}
          <Surface className="jobs-overview-card">
            <Typography variant="h3">Jobs execution overview</Typography>

            <Spacer spacing="l" />

            <div className="jobs-overview-grid">
              {dashboardData?.isJobExecutionDataLoading ? (
                  <div className="jobs-metric">
                    <Typography variant="caption" className="metric-label">Loading execution data...</Typography>
                    <Typography variant="h1" weight="bold" className="metric-value">
                      <LoadingSpinner size="small" noShadow={true} />
                    </Typography>
                  </div>
              ) : (
                Object.entries(jobsMetricData).map(([label, value]) => (
                  <div className="jobs-metric" key={label}>
                    <Typography variant="caption" className="metric-label">{label}</Typography>
                    <Typography variant="h1" weight="bold" className="metric-value">
                      {value}
                    </Typography>
                  </div>
                ))
              )}
            </div>
          </Surface>

          {/* Jobs/Test-case Execution Status Overview */}
          <Surface className="status-overview-card">
            <Typography variant="h3">Jobs/Test-case Execution status overview</Typography>

            {/* Chart View Tabs */}
            <div className="chart-view-tabs">
              <button
                className={`chart-tab ${pieChartView === 'jobs' ? 'active' : ''}`}
                onClick={() => setPieChartView('jobs')}
              >
                Jobs
              </button>
              <button
                className={`chart-tab ${pieChartView === 'iterations' ? 'active' : ''}`}
                onClick={() => setPieChartView('iterations')}
              >
                Test-cases (Iterations)
              </button>
            </div>

            <div className="status-content">
              <div className="pie-chart-container">
                {dashboardData?.isJobExecutionDataLoading ? (
                  <div className="loading-pie-chart">
                    <LoadingSpinner noShadow={true} />
                    <Typography variant="body" style={{ marginTop: '16px', textAlign: 'center' }}>
                      Loading execution status...
                    </Typography>
                  </div>
                ) : (pieChartView === 'jobs' ? totalExecutedJobs > 0 : totalExecutedIterations > 0) ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieChartData.filter(d => d.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        innerRadius={0}
                        outerRadius={120}
                        dataKey="value"
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value, name) => {
                          const percentage = pieChartTotal > 0 ? ((value / pieChartTotal) * 100).toFixed(0) : 0;
                          return [`${value} (${percentage}%)`, name];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data-pie-chart">
                    <Typography variant="body">
                      {pieChartView === 'jobs' ? 'No job executions to visualize' : 'No iteration executions to visualize'}
                    </Typography>
                  </div>
                )}
              </div>

              <div className="chart-legend">
                {dashboardData?.isJobExecutionDataLoading ? (
                  <div className="loading-legend">
                    <Typography variant="body">Loading status breakdown...</Typography>
                  </div>
                ) : (
                  pieChartData.map((item, index) => {
                    const percentage = pieChartTotal > 0 ? ((item.value / pieChartTotal) * 100).toFixed(0) : 0;
                    return (
                      <div key={index} className="legend-item">
                        <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                        <Typography variant="body" className="legend-text">
                          {item.name} {percentage}% ({item.value})
                        </Typography>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </Surface>
        </div>



        {/* Section Divider */}
        <div className="section-divider">
          <div className="divider-line"></div>
        </div>


        {/* Test Suite Executions Section */}
        <Surface className="test-suite-card">
          {/* Title and subtitle */}
          <Typography variant="h3">Test Suite Executions</Typography>
          <Spacer spacing="xs" />
          <Typography variant="body" className="subtitle">
            Execution count for the selected date range
          </Typography>

          <Spacer spacing="l" />

          {/* Interactive Test Suite Metrics */}
          <div className="test-suite-metrics">
            {suiteExecutionCounts.length > 0 ? (
              suiteExecutionCounts.map((metric, index) => (
                <div
                  key={index}
                  className={`test-metric-box ${selectedTestSuite === metric.name ? 'active' : ''}`}
                  onClick={() => handleTestSuiteClick(metric.name)}
                >
                  <Typography variant="caption" className="test-metric-label">
                    {metric.name}
                  </Typography>
                  <Typography variant="h1" weight="bold" className="test-metric-value">
                    {metric.count}
                  </Typography>
                </div>
              ))
            ) : (
              <div className="no-data-message-widget">
                <span>No Test Suite execution data found for the selected filters</span>
              </div>
            )}
          </div>

          {/* Section Divider between buttons and chart */}
          <div className="section-divider">
            <div className="divider-line"></div>
          </div>

          {/* Detailed Test Suite Analysis Section */}
          <Surface className="detailed-analysis-card">
            <div className="widget-header">
              <div className="header-left-content">
                <Typography variant="h3">Detailed Test Suite Analysis: {selectedTestSuite || 'No Suite Selected'}</Typography>
                {selectedTestSuite && (
                  <Typography variant="body" className="subtitle">
                    Click bars to view detailed reports
                  </Typography>
                )}
              </div>
              <div className="drilldown-controls">
                {activeChartView === 'execution-trends' && (
                  <SearchableDropdown
                    options={[
                      { sku: 'all', name: 'All Statuses' },
                      { sku: 'passed', name: 'Passed' },
                      { sku: 'failed', name: 'Failed' },
                    ]}
                    handleInputValue={handleDrilldownStatusChange}
                    value={drilldownStatusFilter}
                    placeholder="Filter by status"
                    disabled={!selectedTestSuite}
                    className="status-filter-dropdown"
                  />
                )}
              </div>
            </div>

            {/* Chart View Tabs */}
            <div className="chart-view-tabs">
              <button
                className={`chart-tab ${activeChartView === 'execution-trends' ? 'active' : ''}`}
                onClick={() => setActiveChartView('execution-trends')}
              >
                Execution Trends
              </button>
              <button
                className={`chart-tab ${activeChartView === 'iteration-details' ? 'active' : ''}`}
                onClick={() => setActiveChartView('iteration-details')}
              >
                Test-case Execution Trends
              </button>
            </div>

            <div className="widget-content">
              {/* Execution Trends View */}
              {activeChartView === 'execution-trends' && (
                <>
                  {isLoadingTestSuiteData ? (
                    <div className="loading-state">
                      <LoadingSpinner />
                      <span>Loading execution trends data...</span>
                    </div>
                  ) : drilldownChartData.length > 0 ? (
                    <div className="bar-chart-container" style={{ width: '100%', overflowX: 'auto' }}>
                      <ResponsiveContainer width={Math.max(1400, drilldownChartData.filter(d => !d.gap).length * 100)} height={420}>
                        <BarChart
                          data={drilldownChartData}
                          margin={{ top: 20, right: 40, left: 20, bottom: 60 }}
                          barCategoryGap="40%"
                          onClick={handleBarChartClick}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e5e7eb"
                            horizontal={true}
                            vertical={false}
                          />
                          <XAxis
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={<CustomizedXAxisTick data={drilldownChartData} />}
                            interval={0}
                            height={80}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 14, fill: '#4b5563' }}
                          />
                          <RechartsTooltip content={<CustomTooltip />} />
                          <Legend
                            verticalAlign="top"
                            align="center"
                            wrapperStyle={{ paddingBottom: '20px' }}
                            payload={[
                              { value: 'Passed Test Cases', type: 'square', color: '#24B291' },
                              { value: 'Failed Test Cases', type: 'square', color: '#F2520A' }
                            ]}
                          />
                          {drilldownStatusFilter !== 'failed' && <Bar dataKey="successful" stackId="a" fill="#24B291" name="Passed Test Cases" barSize={40} />}
                          {drilldownStatusFilter !== 'passed' && <Bar dataKey="failed" stackId="a" fill="#F2520A" name="Failed Test Cases" barSize={40} />}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="no-data-message-widget">
                      <span>
                        {appliedFilters.org ?
                          'Select a test suite tile above to see the detailed analysis.' :
                          'Please select organization to view test suite data'
                        }
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Iteration Details View */}
              {activeChartView === 'iteration-details' && (
                <>
                  {isLoadingIterationDetails ? (
                    <div className="loading-state">
                      <LoadingSpinner />
                      <span>Loading iteration details data...</span>
                    </div>
                  ) : iterationChartData.length > 0 ? (
                    <div className="bar-chart-container">
                      <ResponsiveContainer width="100%" height={420}>
                        <BarChart
                          data={iterationChartData}
                          margin={{ top: 20, right: 40, left: 20, bottom: 60 }}
                          barCategoryGap="40%"
                          onClick={handleBarChartClick}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e5e7eb"
                            horizontal={true}
                            vertical={false}
                          />
                          <XAxis
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={<CustomizedXAxisTick data={iterationChartData} />}
                            interval={0}
                            height={80}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 14, fill: '#4b5563' }}
                          />
                          <RechartsTooltip content={<CustomIterationTooltip />} />
                          <Legend
                            verticalAlign="top"
                            align="center"
                            wrapperStyle={{ paddingBottom: '20px' }}
                            payload={[
                              { value: 'Passed Iterations', type: 'square', color: '#24B291' },
                              { value: 'Failed Iterations', type: 'square', color: '#F2520A' },
                              { value: 'In Progress Iterations', type: 'square', color: '#FFA500' },
                              { value: 'Skipped Iterations', type: 'square', color: '#9CA3AF' }
                            ]}
                          />
                          <Bar dataKey="successIterations" stackId="a" fill="#24B291" name="Passed Iterations" barSize={40} />
                          <Bar dataKey="failedIterations" stackId="a" fill="#F2520A" name="Failed Iterations" barSize={40} />
                          <Bar dataKey="inProgressIterations" stackId="a" fill="#FFA500" name="In Progress Iterations" barSize={40} />
                          <Bar dataKey="skippedIterations" stackId="a" fill="#9CA3AF" name="Skipped Iterations" barSize={40} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="no-data-message-widget">
                      <span>
                        {appliedFilters.org ?
                          'Select a test suite tile above to see the iteration details.' :
                          'Please select organization to view iteration details data'
                        }
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </Surface>
        </Surface>

        {/* Section Divider */}
        <div className="section-divider">
          <div className="divider-line"></div>
        </div>

        {/* Top most failures Section */}
        <Surface className="failures-card">
          <Typography variant="h3">Top most failures</Typography>
          <Spacer spacing="l" />

          {/* Failures Table */}
          <div className="failures-table">
            {/* Table Header */}
            <div className="failures-header">
              <Typography variant="body" className="header-message">Test failure message</Typography>
              <Typography variant="body" className="header-count">Count</Typography>
            </div>

            {/* Table Body */}
            <div className="failures-body">
              {isLoadingTopFailures ? (
                <div className="loading-placeholder">
                  <LoadingSpinner noShadow={true} />
                  <Typography variant="body" style={{ marginTop: '16px', textAlign: 'center' }}>
                    Loading failure data...
                  </Typography>
                </div>
              ) : currentFailures.length > 0 ? (
                currentFailures.map((failure, index) => (
                  <div
                    key={failure.id}
                    className="failure-row clickable"
                    onClick={() => handleFailureClick(failure)}
                    title="Click to view detailed report"
                  >
                    <div className="failure-content">
                      <span className="failure-number">{indexOfFirstFailure + index + 1}.</span>
                      <Typography
                        variant="body"
                        className="failure-message"
                      >
                        {failure.message.length > 200
                          ? `${failure.message.substring(0, 200)}...`
                          : failure.message}
                      </Typography>
                    </div>
                    <Typography variant="body" className="failure-count">
                      {failure.count}
                    </Typography>
                  </div>
                ))
              ) : (
                <div className="no-data-message">No failures to display</div>
              )}
            </div>

            {/* Pagination */}
            {totalFailuresPages > 1 && (
              <div className="failures-pagination">
                <div className="pagination-controls">
                  <button
                    className="pagination-btn prev"
                    onClick={() => handlePageChange(setCurrentFailuresPage, currentFailuresPage - 1, totalFailuresPages)}
                    disabled={currentFailuresPage === 1}
                  >
                    <span>&lt;</span>
                  </button>
                  {[...Array(totalFailuresPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      className={`pagination-btn ${currentFailuresPage === i + 1 ? 'active' : ''}`}
                      onClick={() => handlePageChange(setCurrentFailuresPage, i + 1, totalFailuresPages)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="pagination-btn next"
                    onClick={() => handlePageChange(setCurrentFailuresPage, currentFailuresPage + 1, totalFailuresPages)}
                    disabled={currentFailuresPage === totalFailuresPages}
                  >
                    <span>&gt;</span>
                  </button>
                </div>
                <Typography variant="body" className="pagination-info">
                  {indexOfFirstFailure + 1}-{Math.min(indexOfLastFailure, topFailuresData.length)} of {topFailuresData.length} failures
                </Typography>
              </div>
            )}
          </div>
        </Surface>

        {/* Section Divider */}
        <div className="section-divider">
          <div className="divider-line"></div>
        </div>

        {/* Top most failing jobs Section */}
        <Surface className="failures-card">
          <Typography variant="h3">Top most failing jobs</Typography>
          <Spacer spacing="l" />

          {/* Failing Jobs Table */}
          <div className="failures-table three-column">
            {/* Table Header */}
            <div className="failures-header">
              <Typography variant="body" className="header-message">Job name</Typography>
              <Typography variant="body" className="header-count">Failed count</Typography>
              <Typography variant="body" className="header-count">Total executed</Typography>
            </div>

            {/* Table Body */}
            <div className="failures-body">
              {isLoadingTopFailingJobs ? (
                <div className="loading-placeholder">
                  <LoadingSpinner noShadow={true} />
                  <Typography variant="body" style={{ marginTop: '16px', textAlign: 'center' }}>
                    Loading failing jobs data...
                  </Typography>
                </div>
              ) : currentFailingJobs.length > 0 ? (
                currentFailingJobs.map((job, index) => (
                  <div
                    key={job.id}
                    className="failure-row clickable"
                    onClick={() => handleFailingJobClick(job)}
                    title="Click to view detailed report"
                  >
                    <div className="failure-content">
                      <span className="failure-number">{indexOfFirstFailingJob + index + 1}.</span>
                      <Typography variant="body" className="failure-message">
                        {job.jobName}
                      </Typography>
                    </div>
                    <Typography variant="body" className="failure-count">
                      {job.failedCount}
                    </Typography>
                    <Typography variant="body" className="failure-count">
                      {job.totalExecuted}
                    </Typography>
                  </div>
                ))
              ) : (
                <div className="no-data-message">No failing jobs to display</div>
              )}
            </div>

            {/* Pagination */}
            {totalFailingJobsPages > 1 && (
              <div className="failures-pagination">
                <div className="pagination-controls">
                  <button
                    className="pagination-btn prev"
                    onClick={() => handlePageChange(setCurrentFailingJobsPage, currentFailingJobsPage - 1, totalFailingJobsPages)}
                    disabled={currentFailingJobsPage === 1}
                  >
                    <span>&lt;</span>
                  </button>
                  {[...Array(totalFailingJobsPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      className={`pagination-btn ${currentFailingJobsPage === i + 1 ? 'active' : ''}`}
                      onClick={() => handlePageChange(setCurrentFailingJobsPage, i + 1, totalFailingJobsPages)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="pagination-btn next"
                    onClick={() => handlePageChange(setCurrentFailingJobsPage, currentFailingJobsPage + 1, totalFailingJobsPages)}
                    disabled={currentFailingJobsPage === totalFailingJobsPages}
                  >
                    <span>&gt;</span>
                  </button>
                </div>
                <Typography variant="body" className="pagination-info">
                  {indexOfFirstFailingJob + 1}-{Math.min(indexOfLastFailingJob, topFailingJobsData.length)} of {topFailingJobsData.length} failing jobs
                </Typography>
              </div>
            )}
          </div>
        </Surface>

        {/* Filters Sidebar */}
        {showFilters && (
          <div className="filters-sidebar-overlay">
            <div className="filters-sidebar">
              {/* Header */}
              <div className="filters-header">
                <Typography variant="h3">Filters</Typography>
                <Button variant="ghost" onClick={toggleFilters} className="close-btn">
                  ✕
                </Button>
              </div>

              <Divider />

              <div className="filters-content">
                {filtersModified && (
                  <Surface className="filter-notification">
                    <Typography variant="body">
                      <strong>Filters Changed</strong>
                    </Typography>
                    <Typography variant="body">
                      Click "Apply Filter" to update the dashboard with your new filter selections.
                    </Typography>
                  </Surface>
                )}

                {/* Organization Section */}
                <div className="filter-section">
                  <div className="filter-header">
                    <Typography variant="h4">🏢 Organization</Typography>
                    <Button variant="ghost" onClick={clearOrganization} className="clear-btn">
                      Clear
                    </Button>
                  </div>
                  <SearchableDropdown
                    key={`org-${dropdownKey}`}
                    options={orgOptions}
                    handleInputValue={handleOrgChange}
                    clearValue={clearValue}
                    placeholder="Choose organization"
                    updateClearValue={setClearValue}
                    value={currentFilters.org}
                  />
                </div>

                {/* Workstream Section */}
                <div className="filter-section">
                  <div className="filter-header">
                    <Typography variant="h4">🔧 Workstream</Typography>
                    <Button variant="ghost" onClick={clearWorkstream} className="clear-btn">
                      Clear
                    </Button>
                  </div>
                  <SearchableDropdown
                    key={`workstream-${dropdownKey}`}
                    options={workstreamOptions}
                    handleInputValue={handleWorkstreamChange}
                    clearValue={clearValue}
                    placeholder={currentFilters.org ? "Choose workstream" : "Select organization first"}
                    updateClearValue={setClearValue}
                    disabled={!currentFilters.org}
                    value={currentFilters.workstream}
                  />
                </div>

                {/* Projects Section */}
                <div className="filter-section">
                  <div className="filter-header">
                    <Typography variant="h4">📁 Projects</Typography>
                    <Button variant="ghost" onClick={clearProjects} className="clear-btn">
                      Clear
                    </Button>
                  </div>
                  <ProjectSelection
                    key={`${currentFilters.org}-${currentFilters.workstream}-${projectClearCounter}-${dropdownKey}`}
                    name="projects"
                    options={projectOptions}
                    onChange={handleProjectsChange}
                    initialValue={currentFilters.projects}
                    placeholder={currentFilters.org && currentFilters.workstream ? "Choose projects" : "Select org and workstream first"}
                  />
                </div>

                {/* Team Section */}
                <div className="filter-section">
                  <div className="filter-header">
                    <Typography variant="h4">👥 Team</Typography>
                    <Button variant="ghost" onClick={clearTeam} className="clear-btn">
                      Clear
                    </Button>
                  </div>
                  <SearchableDropdown
                    key={`team-${dropdownKey}`}
                    options={teamOptions}
                    handleInputValue={handleTeamChange}
                    clearValue={clearValue}
                    placeholder={currentFilters.workstream ? "Choose team" : "Select workstream first"}
                    updateClearValue={setClearValue}
                    disabled={!currentFilters.workstream}
                    value={currentFilters.team}
                  />
                </div>

                {/* Date Range Section */}
                <div className="filter-section">
                  <Typography variant="h4">📅 Date Range</Typography>
                  <div className="date-range-inputs">
                    <div className="date-input-group">
                      <Typography variant="body">Start Date</Typography>
                      <input
                        type="date"
                        value={currentFilters.dateRange.startDate}
                        onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                        className="date-input"
                        min={minDate}
                        max={maxDate}
                      />
                    </div>
                    <div className="date-input-group">
                      <Typography variant="body">End Date</Typography>
                      <input
                        type="date"
                        value={currentFilters.dateRange.endDate}
                        onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                        className="date-input"
                        min={minDate}
                        max={maxDate}
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="filter-actions">
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="clear-all-btn"
                  >
                    Clear all
                  </Button>
                  <Button
                    variant="solid"
                    onClick={applyFilters}
                    className="apply-filter-btn"
                  >
                    {filtersModified ? 'Apply Filter' : 'Apply Filter'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ThemeContext.Provider>
  );
};

export default DashboardV2;
